import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

// A small Markdown renderer for our own documents (docs/sales/*.md shown in the staff pages): headings, paragraphs,
// bold, italics, code, links, bullet/numbered/check lists (nested by indentation), quotes, tables, rules, code blocks.
// It builds React elements only (never HTML strings), so nothing in a document can inject markup. Bracketed
// placeholders such as [Nom] are highlighted: they are the parts to fill in before a call or a signature.

export type LinkResolver = (href: string) => { to: string } | { href: string } | null;

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'rule' }
  | { kind: 'code'; text: string }
  | { kind: 'quote'; blocks: Block[] }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'list'; items: ListItem[]; ordered: boolean };
type ListItem = { text: string; checked: boolean | null; children: ListItem[]; ordered: boolean };

const LIST = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const isTableDivider = (line: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

function parse(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) { i++; continue; }
    if (line.startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.startsWith('```')) body.push(lines[i++]!);
      i++;
      blocks.push({ kind: 'code', text: body.join('\n') });
      continue;
    }
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) { blocks.push({ kind: 'rule' }); i++; continue; }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) { blocks.push({ kind: 'heading', level: h[1]!.length, text: h[2]! }); i++; continue; }
    if (line.trim().startsWith('|') && i + 1 < lines.length && isTableDivider(lines[i + 1]!)) {
      const header = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.trim().startsWith('|')) rows.push(cells(lines[i++]!));
      blocks.push({ kind: 'table', header, rows });
      continue;
    }
    if (line.startsWith('>')) {
      const inner: string[] = [];
      while (i < lines.length && lines[i]!.startsWith('>')) inner.push(lines[i++]!.replace(/^>\s?/, ''));
      blocks.push({ kind: 'quote', blocks: parse(inner) });
      continue;
    }
    if (LIST.test(line)) {
      const raw: { indent: number; text: string; ordered: boolean }[] = [];
      while (i < lines.length && lines[i]!.trim()) {
        const m = LIST.exec(lines[i]!);
        if (m) raw.push({ indent: m[1]!.length, text: m[3]!, ordered: /\d/.test(m[2]!) });
        else if (/^\s{2,}\S/.test(lines[i]!) && raw.length) raw[raw.length - 1]!.text += ' ' + lines[i]!.trim();
        else break;
        i++;
      }
      const items = nest(raw);
      blocks.push({ kind: 'list', items, ordered: items[0]?.ordered ?? false });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,4}\s|>|```|\s*\|)/.test(lines[i]!) && !LIST.test(lines[i]!)) para.push(lines[i++]!.trim());
    blocks.push({ kind: 'paragraph', text: para.join(' ') });
  }
  return blocks;
}

function nest(raw: { indent: number; text: string; ordered: boolean }[]): ListItem[] {
  const root: ListItem[] = [];
  const stack: { indent: number; items: ListItem[] }[] = [{ indent: -1, items: root }];
  for (const r of raw) {
    while (stack.length > 1 && r.indent <= stack[stack.length - 1]!.indent) stack.pop();
    const check = /^\[( |x|X)\]\s+(.*)$/.exec(r.text);
    const item: ListItem = { text: check ? check[2]! : r.text, checked: check ? check[1] !== ' ' : null, children: [], ordered: r.ordered };
    stack[stack.length - 1]!.items.push(item);
    stack.push({ indent: r.indent, items: item.children });
  }
  return root;
}

const INLINE = /(`[^`]+`)|(\*\*[^*]+?\*\*)|(\[[^\]]+\]\([^)\s]+\))|(\[[^\]]*\])|(\*[^*\s][^*]*?\*)/g;

function inline(text: string, resolve: LinkResolver): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [t] = m;
    if (m[1]) out.push(<code key={k++}>{t.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={k++}>{inline(t.slice(2, -2), resolve)}</strong>);
    else if (m[3]) {
      const label = t.slice(1, t.indexOf(']('));
      const href = t.slice(t.indexOf('](') + 2, -1);
      const target = resolve(href);
      if (target && 'to' in target) out.push(<Link key={k++} to={target.to}>{inline(label, resolve)}</Link>);
      else if (target) out.push(<a key={k++} href={target.href} target="_blank" rel="noreferrer">{inline(label, resolve)}</a>);
      else out.push(<Fragment key={k++}>{inline(label, resolve)}</Fragment>);
    } else if (m[4]) out.push(<mark key={k++} className="md-ph">{t}</mark>);
    else if (m[5]) out.push(<em key={k++}>{inline(t.slice(1, -1), resolve)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function List({ items, ordered, resolve }: { items: ListItem[]; ordered: boolean; resolve: LinkResolver }) {
  const body = items.map((it, i) => (
    <li key={i} className={it.checked !== null ? 'md-check' : undefined}>
      {it.checked !== null && <input type="checkbox" defaultChecked={it.checked} aria-label={it.text} />}
      {inline(it.text, resolve)}
      {it.children.length > 0 && <List items={it.children} ordered={it.children[0]!.ordered} resolve={resolve} />}
    </li>
  ));
  return ordered ? <ol>{body}</ol> : <ul>{body}</ul>;
}

function render(blocks: Block[], resolve: LinkResolver): ReactNode[] {
  return blocks.map((b, i) => {
    switch (b.kind) {
      case 'heading': {
        const H = (['h1', 'h2', 'h3', 'h4'] as const)[b.level - 1]!;
        return <H key={i}>{inline(b.text, resolve)}</H>;
      }
      case 'paragraph':
        return <p key={i}>{inline(b.text, resolve)}</p>;
      case 'rule':
        return <hr key={i} />;
      case 'code':
        return <pre key={i}>{b.text}</pre>;
      case 'quote':
        return <blockquote key={i}>{render(b.blocks, resolve)}</blockquote>;
      case 'list':
        return <List key={i} items={b.items} ordered={b.ordered} resolve={resolve} />;
      case 'table':
        return (
          <div key={i} className="md-table">
            <table className="table">
              <thead>
                <tr>{b.header.map((c, j) => <th key={j}>{inline(c, resolve)}</th>)}</tr>
              </thead>
              <tbody>
                {b.rows.map((r, j) => (
                  <tr key={j}>{r.map((c, n) => <td key={n}>{inline(c, resolve)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}

/** Renders a Markdown document; `resolve` decides where each link goes (null: plain text). */
export function Markdown({ source, resolve }: { source: string; resolve: LinkResolver }) {
  return <div className="md-doc">{render(parse(source.replace(/\r\n/g, '\n').split('\n')), resolve)}</div>;
}
